<template>
  <div class="configuracoes-ia-settings-form">
    <!-- (a) Max-context section -->
    <div
      v-if="maxContextLoaded"
      class="settings-section"
      data-testid="config-ia-billing-settings-form"
    >
      <h4 class="section-title">{{ t('Configurações de Contexto') }}</h4>
      <div class="form-fields">
        <input-number
          v-model="maxContext"
          elem-id="config-ia-max-context"
          :label="t('Máximo de contexto (turnos)')"
          data-testid="config-ia-max-context-input"
          :min="1"
          :max="100"
        />
      </div>
      <div class="form-actions">
        <Button
          :label="t('Salvar configurações')"
          icon="pi pi-save"
          severity="primary"
          :loading="savingAgentSettings"
          data-testid="config-ia-billing-settings-save-button"
          @click="handleSaveContextSettings"
        />
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { useAiSettings } from '../../../composables/useAiSettings'
import { emitValidationFailed } from '../../../composables/useFormValidation'
import { log } from '../../../utils/DebugLogger'

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'validation-failed'): void
}>()

const {
  agentSettings,
  savingAgentSettings,
  loadAgentSettings,
  saveAgentSettings,
} = useAiSettings()

// ── Local form state ─────────────────────────────────────────────────────────

const maxContextLoaded = ref(false)

const maxContext = ref<number | null>(null)

// ── Validation ───────────────────────────────────────────────────────────────

function validateContextSettings(): boolean {
  const errors: Record<string, string> = {}
  // A null maxContext means "no value configured / do not change it" (per
  // AiCredits_AgentSettingsInput — the field is optional and omitting it
  // leaves the setting unchanged) — that is NOT a validation error. Only a
  // PRESENT-but-invalid value (0, negative, non-integer) is rejected.
  if (maxContext.value !== null && (!Number.isInteger(maxContext.value) || maxContext.value < 1)) {
    errors.max_context = t('Máximo de contexto deve ser um número inteiro de pelo menos 1')
  }
  if (Object.values(errors).some(Boolean)) {
    emitValidationFailed('ConfiguracoesIaSettingsForm', errors)
    emit('validation-failed')
    // This form is inline on StaffCreditsPage (not inside a modal), so the
    // sole eventBus listener for 'form.validation.failed' (src/ui/Modal.vue)
    // never sees it — surface the failure directly, naming the field.
    eventBus.emit('globalMessage.error', Object.values(errors).filter(Boolean).join(' '))
    return false
  }
  return true
}

// ── Handlers ─────────────────────────────────────────────────────────────────

async function handleSaveContextSettings(): Promise<void> {
  if (!validateContextSettings()) return

  // A null maxContext means "leave unchanged" — nothing to send.
  if (maxContext.value === null) return

  try {
    await saveAgentSettings({ ai_max_context: maxContext.value })
    eventBus.emit('globalMessage.success', t('Configurações salvas com sucesso'))
  } catch (err) {
    log('ConfiguracoesIaSettingsForm', 'handleSaveContextSettings', 'Erro ao salvar contexto máximo', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar contexto máximo'))
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(async () => {
  // Load agent settings — degrade gracefully on failure. ai_max_context is
  // optional/null when the config row is absent — leave the input empty
  // rather than crashing or showing NaN/0.
  try {
    await loadAgentSettings()
    maxContext.value = agentSettings.value?.ai_max_context ?? null
    maxContextLoaded.value = true
  } catch (err) {
    log('ConfiguracoesIaSettingsForm', 'mounted', 'Agent settings indisponíveis', err)
  }
})
</script>

<style scoped>
.configuracoes-ia-settings-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.settings-section {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  padding: 20px;
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--p-text-color);
}

.form-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.peg-hint {
  margin: 8px 0 0 0;
  font-size: 13px;
  color: var(--p-text-muted-color, #6b7280);
}
</style>
